import {debounceTime, distinctUntilChanged} from 'rxjs/operators';
import {Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {UntypedFormControl} from '@angular/forms';
import {Subject} from 'rxjs';
import {SortUtils} from '../../utils/sort-utils';
import {Embed} from '../../domain/embed.model';
import { VideosService } from '../../services/videos.service';
import { SnackBarService } from 'src/app/core/services/snackbar.service';

@Component({
  selector: 'ctv-tags-autocomplete',
  template: `
    <mat-form-field [floatLabel]="floatLabel || 'always'" class="w-100" [ngClass]="formFieldClass">
      <mat-label *ngIf="floatLabel !== 'never'">{{label ? label : 'Select tag'}}</mat-label>
      <input matInput
             #name
             autocomplete="off"
             (focus)="onFocusEvent()"
             aria-label="Tag"
             [matAutocomplete]="auto"
             [formControl]="filterCtrl"
             placeholder="{{floatLabel !== 'never' ? 'Search' : 'Select tag'}}">
      <i class="fas fa-spinner fa-spin" matSuffix [hidden]="!serverSearching"></i>
      <mat-autocomplete #auto="matAutocomplete"
                        (optionSelected)="optionSelected($event)"
                        [displayWith]="display">
        <mat-option *ngFor="let item of filteredItems | async" [value]="item">
          {{item}}
        </mat-option>
      </mat-autocomplete>
    </mat-form-field>`,
})
export class TagsAutocompleteComponent implements OnInit {
  @ViewChild('name', {static: true}) nameFilter: ElementRef;

  @Input()
  public placeholder: string;

  @Input()
  public name: string;

  @Input()
  public activeTags: string[];

  @Input()
  public floatLabel: string;

  @Input()
  public label: string;

  @Input()
  public formFieldClass: string;

  @Input()
  public availableTags: string[];

  @Input()
  items: string[];

  @Output()
  tagSelected = new EventEmitter<string>();

  serverSearching = false;

  filteredItems: Subject<string[]>;
  filterCtrl = new UntypedFormControl();
  selectedTag;
  allTags: string[] = [];
  filteredTags: string[] = [];
  availableTagsFlag = false;

  constructor(private service: VideosService, private snackBarService: SnackBarService,) {
    this.filteredItems = new Subject();
    this.filterCtrl.valueChanges.pipe(
      debounceTime(800),
      distinctUntilChanged())
      .subscribe(next => {
        this.filteredItems.next([]);
        if (!next && this.selectedTag) {
          this.selectedTag = undefined;
          this.emit();
        }

        if (next && typeof next !== 'object') {
          this.serverSearching = true;
          this.filterCtrl.disable();
          this.searchTag();
        }
      });
  }

  resetInput() {
    this.selectedTag = null;
    this.filterCtrl.setValue(null);
  }

  ngOnInit(): void {
    if (this.name) {
      this.selectedTag = this.name;
      this.filterCtrl.setValue(this.name);
      this.emit();
    }
    this.filteredTags = this.activeTags;
  }

  onFocusEvent() {
    if (this.availableTagsFlag) {
      this.allTags = this.availableTags;
    } else {
      if (!this.allTags.length) {
        this.getAllTallTags();
        return;
      }
    }
    if (!this.filterCtrl.value?.length) {
      this.filteredItems.next(this.allTags.filter(item => !this.filteredTags.includes(item)));
    }
  }

  display(e?: Embed) {
    return e ? e.name : undefined;
  }

  optionSelected($event) {
    this.selectedTag = $event.option.value;
    this.filteredItems.next([]);
    this.nameFilter.nativeElement.blur();
    this.emit();
  }

  private emit() {
    this.tagSelected.emit(this.selectedTag);
  }

  getAllTallTags() {
    this.service.tagsList().subscribe((response) => {
      this.allTags = response;
      if (!this.filterCtrl.value?.length) {
        this.filteredItems.next(this.allTags.filter(item => !this.filteredTags.includes(item)));
      }
    }, e => {
      this.snackBarService.error('Error occurred during getting tags');
    });
  }

  updateFilteredTags(items) {
    this.filteredTags = items.map(item => item);
  }

  searchTag() {
    if (this.allTags.length > 0) {
      const search = this.allTags.filter(e => e.toLowerCase().includes(this.filterCtrl.value.toLowerCase()));
      this.filteredItems.next(search.sort(SortUtils.propertyComparatorString('name')).splice(0, 10));
    }

    this.filterCtrl.enable();
    this.nameFilter.nativeElement.focus();
    this.serverSearching = false;
  }
}
